<script setup>
import {ref, reactive, onMounted, onUnmounted, getCurrentInstance, computed} from 'vue';
import Swal from 'sweetalert2';
import $http from "@/axios.config";
import store from "@/store";
import OrderForm from "@/components/forms/OrderForm.vue";
import OrderInWork from "@/components/items/OrderInWork.vue";
import {useI18n} from 'vue-i18n';
import {watch} from 'vue';
import {log} from "@/utils/clientLogger.js";

const {t} = useI18n({useScope: 'global'});
const userRole = computed(() => store.getters.userData?.role?.name)
const isDistributor = computed(() => userRole.value === 'distributor')
const closeButton = ref(null);
const activeOrder = reactive({});
const orders = ref([]);
const ordersInWork = ref([]);
const orderFormRef = ref(null);
const ordersInDone = ref([]);
const instance = getCurrentInstance();

const onlineUsers = ref([]);
const isOnlineBlockOpen = ref(false);

const filters = reactive({
  managerId: store.getters.userData?.role?.name === 'manager' ? store.getters.userData?._id : '',
  draftsmanId: ''
});

const onlineCount = computed(() => {
  return onlineUsers.value.filter(u => u.role && u.role.name === 'draftsman').length;
});

watch([() => filters.managerId, () => filters.draftsmanId], () => {
  getNewOrders();
  getOrdersInWork();
  getOrdersInDone();
});

function closeModal() {
  if (closeButton.value) {
    closeButton.value.click();
  }
}

function postOrder(data) {
  $http.post('api/orders', data).then(() => {
    Swal.fire({
      icon: 'success',
    });
    orderFormRef.value?.resetForm();
    closeModal();
  }).catch(error => {
    log.error('View:HomePage:postOrder', {'error': error})
  }).finally(() => {
    orderFormRef.value?.submissionComplete();
  });
}

function handleOrderTaken(orderId) {
  orders.value = orders.value.filter(order => order._id !== orderId);
  getOrdersInWork();
}

onMounted(() => {
  const socket = instance.appContext.config.globalProperties.$socket;

  getNewOrders();
  getOrdersInWork();
  getOrdersInDone();
  fetchUsers();

  socket.on("update-order", () => {
    getNewOrders();
    getOrdersInWork();
    getOrdersInDone();
  });

  socket.on('online-users', (usersArray) => {
    onlineUsers.value = usersArray;
  });

  socket.emit('get-online-users');
});

onUnmounted(() => {
  const socket = instance.appContext.config.globalProperties.$socket;
  socket.off("update-order");

  socket.off('online-users');
});

async function getNewOrders() {
  const requestParams = {
    params: {
      managerId: filters.managerId,
      draftsmanId: filters.draftsmanId,
      sort: {
        updatedAt: -1,
        isUrgent: -1
      }
    }
  };

  const [activeResult, cuttingResult] = await Promise.allSettled([
    $http.get('api/orders/status/active', requestParams),
    $http.get('api/orders/status/cutting', requestParams)
  ]);

  let allNewOrders = [];

  if (activeResult.status === 'fulfilled') {
    allNewOrders = [...allNewOrders, ...activeResult.value.data];
  } else {
    console.error("Помилка завантаження 'active' замовлень:", activeResult.reason);
  }

  if (cuttingResult.status === 'fulfilled') {
    allNewOrders = [...allNewOrders, ...cuttingResult.value.data];
  } else {
    console.warn("Помилка завантаження 'cutting' замовлень:", cuttingResult.reason);
  }

  orders.value = allNewOrders;
}

function getOrdersInWork() {
  $http.get('api/orders/status/work', {
    params: {
      managerId: filters.managerId,
      draftsmanId: filters.draftsmanId,
      sort: {
        updatedAt: -1,
        isUrgent: -1
      }
    }
  }).then((response) => {
    ordersInWork.value = response.data;
  });
}

function getOrdersInDone() {
  $http.get('api/orders/status/done', {
    params: {
      sort: {
        updatedAt: 1,
        isUrgent: -1
      },
      managerId: filters.managerId,
      draftsmanId: filters.draftsmanId
    }
  }).then((response) => {
    ordersInDone.value = response.data;
  });
}

function fetchUsers() {
  $http.get('api/users?limit=0').then(res => {
    const allUsers = res.data.data;

    const managers = allUsers.filter(user => user.role?.name === 'manager');
    const draftsmen = allUsers.filter(user => user.role?.name === 'draftsman');

    store.commit('lang/SET_USERS_MANAGER', managers);
    store.commit('lang/SET_USERS_DRAFTSMAN', draftsmen);
  });
}

const filteredOrders = computed(() => {
  return orders.value
      .filter(order => {
        const byManager = filters.managerId ? order.managerId?._id === filters.managerId : true;
        const byDraftsman = filters.draftsmanId ? order.draftsmanId?._id === filters.draftsmanId : true;
        return byManager && byDraftsman;
      })
      .sort((a, b) => {

        const getPriorityScore = (order) => {
          const isDistributor = order.managerId?.role?.name === 'distributor';
          const isUrgent = order.isUrgent;

          if (isDistributor) return 3;
          if (isUrgent) return 2;
          return 1;
        };

        const scoreA = getPriorityScore(a);
        const scoreB = getPriorityScore(b);

        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }

        return new Date(a.createdAt) - new Date(b.createdAt);
      });
});

const filteredOrdersInWork = computed(() => {
  return ordersInWork.value
      .filter(order => {
        const byManager = filters.managerId ? order.managerId?._id === filters.managerId : true;
        const byDraftsman = filters.draftsmanId ? order.draftsmanId?._id === filters.draftsmanId : true;
        return byManager && byDraftsman;
      })
      .sort((a, b) => {

        const getPriorityScore = (order) => {
          const isDistributor = order.managerId?.role?.name === 'distributor';
          const isUrgent = order.isUrgent;

          if (isDistributor) return 3;
          if (isUrgent) return 2;
          return 1;
        };

        const scoreA = getPriorityScore(a);
        const scoreB = getPriorityScore(b);

        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }

        return new Date(a.createdAt) - new Date(b.createdAt);
      });
});

const filteredOrdersInDone = computed(() => {
  return ordersInDone.value
      .filter(order => {
        const byManager = filters.managerId ? order.managerId?._id === filters.managerId : true;
        const byDraftsman = filters.draftsmanId ? order.draftsmanId?._id === filters.draftsmanId : true;
        return byManager && byDraftsman;
      })
      .sort((a, b) => {
        const toNum = (val) => (val ? 1 : 0);

        const aIsDist = toNum(a.managerId?.role?.name === 'distributor');
        const bIsDist = toNum(b.managerId?.role?.name === 'distributor');
        if (aIsDist !== bIsDist) {
          return bIsDist - aIsDist;
        }

        const aIsUrgent = toNum(a.isUrgent);
        const bIsUrgent = toNum(b.isUrgent);
        if (aIsUrgent !== bIsUrgent) {
          return bIsUrgent - aIsUrgent;
        }

        return 0;
      });
});
</script>

<template>
  <div class="container mt-3">
    <div class="row mb-4 justify-content-center">
      <div class="col-12 mb-4">
        <div class="row align-items-center">
          <div :class="isDistributor ? 'col-12' : 'col-sm-6 mb-3 mb-sm-0'">
            <button
                v-if="store.getters.isManager || store.getters.isDraftsman || isDistributor"
                type="button"
                class="loading btn btn-outline-success w-100 d-block"
                data-bs-toggle="modal"
                data-bs-target="#orderModal">
              + {{ t('buttons.addOrder') }}
            </button>
          </div>
          <div class="col-sm-6" v-if="!isDistributor">
            <div class="border rounded bg-light-subtle p-3">
              <div class="d-flex justify-content-between align-items-center collapse-toggle" @click="isOnlineBlockOpen = !isOnlineBlockOpen">
                <h5 class="mb-0 d-flex align-items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-wifi text-success me-2">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M12 18l.01 0"/>
                    <path d="M9.172 15.172a4 4 0 0 1 5.656 0"/>
                    <path d="M6.343 12.343a8 8 0 0 1 11.314 0"/>
                    <path d="M3.515 9.515a12 12 0 0 1 16.97 0"/>
                  </svg>
                  {{ t('labels.onlineUsers') }}
                  <span class="badge text-bg-success ms-2">{{ onlineCount }}</span>
                </h5>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down chevron">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M6 9l6 6l6 -6"/>
                </svg>
              </div>

              <div v-if="isOnlineBlockOpen" class="pt-3">
                <div class="row">
                  <!--                  <div class="col-md-6">-->
                  <!--                    <strong>{{ t('tableheadings.manager') }}:</strong>-->
                  <!--                    <div class="mt-2 d-flex flex-wrap gap-2">-->
                  <!--                      <template v-for="user in onlineUsers.filter(u => u.role && u.role.name === 'manager')" :key="user._id">-->
                  <!--                        <span class="badge text-bg-success d-flex align-items-center">-->
                  <!--                          {{ user.username }}-->
                  <!--                          <small v-if="user.currentStatus" class="ms-1 ps-1 border-start border-light border-opacity-25 d-flex align-items-center">-->
                  <!--                            <span class="d-inline-block me-1" :style="{ backgroundColor: user.currentStatus.color, width: '10px', height: '10px', borderRadius: '50%' }"></span>-->
                  <!--                            {{ user.currentStatus.name }}-->
                  <!--                          </small>-->
                  <!--                        </span>-->
                  <!--                      </template>-->
                  <!--                      <span v-if="!onlineUsers.some(u => u.role && u.role.name === 'manager')" class="text-muted small">-->
                  <!--                  {{ t('messages.noManagersOnline') }}-->
                  <!--                </span>-->
                  <!--                    </div>-->
                  <!--                  </div>-->

                  <div class="col-12">
                    <strong>{{ t('tableheadings.draftsman') }}:</strong>
                    <div class="mt-2 d-flex flex-wrap gap-2">
                      <template v-for="user in onlineUsers.filter(u => u.role && u.role.name === 'draftsman')" :key="user._id">
                        <span class="badge text-bg-success d-flex align-items-center">
                          {{ user.username }}
                          <small v-if="user.currentStatus" class="ms-1 ps-1 border-start border-light border-opacity-25 d-flex align-items-center">
                            <span class="d-inline-block me-1" :style="{ backgroundColor: user.currentStatus.color, width: '10px', height: '10px', borderRadius: '50%' }"></span>
                            {{ user.currentStatus.name }}
                          </small>
                        </span>
                      </template>
                      <span v-if="!onlineUsers.some(u => u.role && u.role.name === 'draftsman')" class="text-muted small">
                        {{ t('messages.noDraftsmenOnline') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-3" v-if="!isDistributor">
        <div class="col-md-6">
          <label>{{ t('tableheadings.manager') }}</label>
          <select v-model="filters.managerId" class="form-select">
            <option value="">{{ t('tableheadings.all') }}</option>
            <option v-for="user in store.getters['lang/usersManager']" :key="user._id" :value="user._id">
              {{ user.username }}
            </option>
          </select>
        </div>
        <div class="col-md-6">
          <label>{{ t('tableheadings.draftsman') }}</label>
          <select v-model="filters.draftsmanId" class="form-select">
            <option value="">{{ t('tableheadings.all') }}</option>
            <option v-for="user in store.getters['lang/usersDraftsman']" :key="user._id" :value="user._id">
              {{ user.username }}
            </option>
          </select>
        </div>
      </div>

      <div class="col-md-4">
        <h3 class="col-12 text-center">{{ t('headings.newOrders') }}</h3>
        <div class="list-group mt-3">
          <OrderInWork v-for="order in filteredOrders" :key="order.id" :order="order" @order-taken="handleOrderTaken"/>
        </div>
      </div>

      <div class="col-md-4">
        <h3 class="col-12 text-center">{{ t('headings.ordersInProgress') }}</h3>
        <div class="list-group mt-3 row">
          <OrderInWork v-for="order in filteredOrdersInWork" :key="order.id" :order="order"/>
        </div>
      </div>

      <div class="col-md-4">
        <h3 class="col-12 text-center">{{ t('headings.completedOrders') }}</h3>
        <div class="list-group mt-3">
          <OrderInWork v-for="order in filteredOrdersInDone" :key="order.id" :order="order"/>
        </div>
      </div>
    </div>

    <div class="modal fade" id="orderModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-labelledby="orderModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-scrollable modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="orderModalLabel">
              <span v-if="activeOrder.id">{{ t('modal.editOrder') }}</span>
              <span v-else>{{ t('modal.addOrder') }}</span>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" ref="closeButton" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <OrderForm ref="orderFormRef" @submitOrder="postOrder" :hide-attachments-upload="true" :compact="true"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.collapse-toggle {
  cursor: pointer;
  transition: background-color 0.2s ease;

  .chevron {
    transition: transform 0.3s ease;
  }

  .chevron.is-open {
    transform: rotate(180deg);
  }
}
</style>